import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AuthLink as Link } from '../auth/AuthContext';
import { CheckCircle, AlertTriangle, MailCheck } from 'lucide-react';
import api from '../api';
import AuthCard from '../components/AuthCard';
import Spinner from '../components/Spinner';

/**
 * Второй шаг смены адреса: сюда приводит ссылка из письма, отправленного на прежний адрес.
 * Новый адрес спрашивается только здесь - до подтверждения владения прежним ящиком
 * спрашивать его не о чем.
 */
const ChangeEmailPage = () => {
    const [params] = useSearchParams();
    const token = params.get('token') || '';

    const [state, setState] = useState('checking'); // 'checking' | 'form' | 'invalid' | 'done'
    const [email, setEmail] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!token) {
            setState('invalid');
            return;
        }
        api.get('/api/auth/email/change/check', { params: { token } })
            .then(res => setState(res.data.valid ? 'form' : 'invalid'))
            .catch(() => setState('invalid'));
    }, [token]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (saving) return;

        setSaving(true);
        setError(null);
        try {
            await api.post('/api/auth/email/change/apply', { token, email });
            setState('done');
        } catch (err) {
            const status = err.response?.status;
            if (status === 409) {
                setError('Этот адрес уже занят другим аккаунтом');
            } else if (status === 400) {
                setError(err.response?.data?.message || 'Проверьте адрес: возможно, он совпадает с текущим или ссылка устарела');
            } else {
                setError('Не удалось сменить адрес. Попробуйте позже.');
            }
        } finally {
            setSaving(false);
        }
    };

    if (state === 'checking') {
        return (
            <AuthCard title="Смена адреса почты">
                <div className="flex items-center justify-center gap-3 text-gray-500 py-4">
                    <Spinner size={22} /> Проверяем ссылку…
                </div>
            </AuthCard>
        );
    }

    if (state === 'invalid') {
        return (
            <AuthCard title="Ссылка не работает">
                <AlertTriangle size={44} className="text-amber-500 mx-auto mb-4" />
                <p className="text-gray-600 text-sm leading-relaxed">
                    Ссылка для смены адреса устарела или уже использована. Запросите смену заново
                    в разделе профиля - адрес аккаунта при этом не изменился.
                </p>
                <Link
                    to="/dashboard/profile"
                    className="mt-6 inline-block bg-blue-600 text-white px-4 py-2.5 rounded-lg text-sm font-medium no-underline hover:bg-blue-700"
                >
                    В личный кабинет
                </Link>
            </AuthCard>
        );
    }

    if (state === 'done') {
        return (
            <AuthCard title="Адрес изменён">
                <CheckCircle size={44} className="text-emerald-600 mx-auto mb-4" />
                <p className="text-gray-600 text-sm leading-relaxed">
                    Теперь аккаунт привязан к адресу <span className="font-medium text-gray-900">{email}</span>.
                </p>
                <div className="mt-4 flex items-start gap-2.5 text-left bg-blue-50 border border-blue-200 text-blue-900 rounded-lg p-3 text-[13px]">
                    <MailCheck size={18} className="shrink-0 mt-0.5" />
                    <span>
                        Новый адрес пока не подтверждён - на него отправлено письмо со ссылкой.
                        До подтверждения восстановление пароля по нему работать не будет.
                    </span>
                </div>
                <Link
                    to="/dashboard/profile"
                    className="mt-6 inline-block bg-blue-600 text-white px-4 py-2.5 rounded-lg text-sm font-medium no-underline hover:bg-blue-700"
                >
                    В личный кабинет
                </Link>
            </AuthCard>
        );
    }

    return (
        <AuthCard title="Новый адрес почты">
            <p className="text-gray-600 text-sm mb-5 leading-relaxed">
                Укажите адрес, к которому привязать аккаунт. На него уйдёт письмо с подтверждением.
            </p>
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <input
                    className="p-3 border border-gray-300 rounded-lg text-base transition-colors focus:border-blue-500 focus:outline-none disabled:bg-gray-50"
                    type="email"
                    placeholder="Новый email"
                    value={email}
                    disabled={saving}
                    onChange={e => setEmail(e.target.value)}
                    required
                    autoFocus
                />
                {error && <div className="text-red-600 bg-red-50 p-3 rounded-md text-sm">{error}</div>}
                <button
                    type="submit"
                    disabled={saving}
                    className="bg-blue-600 text-white p-3 border-none rounded-lg text-base font-semibold transition-colors shadow-sm flex items-center justify-center gap-2 enabled:cursor-pointer enabled:hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-default"
                >
                    {saving && <Spinner size={18} />}
                    {saving ? 'Сохраняем…' : 'Сменить адрес'}
                </button>
            </form>
        </AuthCard>
    );
};

export default ChangeEmailPage;
